import type { AuthProvider } from "@refinedev/core";

import { clearAccessToken, getAccessToken, setAccessToken } from "./auth/tokenStore";
import { roleFromAuthorities, rolesFromAuthorities } from "./auth/roleFromAuthorities";
import { clearCsrfToken, csrfHeaders } from "./auth/csrf";
import { FOSTER_LOGIN_PATH } from "./auth/loginRoute";

// jmix-exit E3b.1: the real Refine AuthProvider over the Epic-2 `/api/auth/*` bridge, replacing the
// Epic-3a stub (auth/stubAuth.ts). Modelled on backoffice's frontend/src/authProvider.ts but adapted
// to platform's TWO first-party login flows (admin username/password + foster magic-code) and NO
// remember/OIDC-PKCE (same-origin first-party flows — the PKCE-not-applicable decision is recorded in
// jmix-3b-2). The access token lives in memory only (tokenStore); silent restore rides the httpOnly
// refresh cookie. The server-side refresh rotation / reuse-detection / revoking-logout hardening is
// the sibling story jmix-3b-2; here logout is best-effort (clears the in-memory token regardless).

// Same base-URL convention as dataProvider: the Vite dev server proxies /api to the API; the
// single-artifact deploy serves both from one origin (same-origin → SameSite=Strict cookies suffice).
const API_URL = (import.meta.env.VITE_API_URL as string | undefined) ?? "/api";

/** Refine login params — discriminated by which fields are present (admin vs foster verify). */
export type LoginParams =
  | { username: string; password: string; remember?: boolean }
  | { email: string; code: string };

function isAdminLogin(
  p: LoginParams,
): p is { username: string; password: string; remember?: boolean } {
  return "username" in p && "password" in p;
}

/** Decode a JWT payload (display only — never a signature check; the API validates the signature). */
export function decodeJwtClaims(token: string): Record<string, unknown> | undefined {
  const parts = token.split(".");
  if (parts.length !== 3) {
    return undefined;
  }
  try {
    const payload = parts[1].replace(/-/g, "+").replace(/_/g, "/");
    return JSON.parse(atob(payload)) as Record<string, unknown>;
  } catch {
    return undefined;
  }
}

/** The `authorities` claim as a string[] (the SPA token carries `ROLE_<name>` strings). */
export function authoritiesFromToken(token: string | undefined): string[] {
  if (!token) {
    return [];
  }
  const claims = decodeJwtClaims(token);
  const authorities = claims?.authorities;
  if (Array.isArray(authorities)) {
    return authorities as string[];
  }
  if (typeof authorities === "string") {
    return authorities.split(" ").filter(Boolean);
  }
  return [];
}

type TokenResponse = { access_token?: string };

/**
 * Step 1 of the foster magic-code flow: request a 6-digit code for an email. The API answers 204 for
 * BOTH known and unknown emails (anti-enumeration) — so the SPA must NEVER reveal whether the email
 * exists; a 2xx always advances to the code step. 429 surfaces a rate-limit message.
 *
 * On test/acc the API runs with `fawkes.dev.expose-magic-codes` (no mail delivery there) and answers
 * 200 with `{devCode}` instead of 204; the login screen then shows the code directly.
 */
export async function requestMagicCode(
  email: string,
): Promise<{ ok: true; devCode?: string } | { ok: false; rateLimited?: boolean; message: string }> {
  if (!email) {
    return { ok: false, message: "enterEmail" };
  }
  let response: Response;
  try {
    response = await fetch(`${API_URL}/auth/magic/request`, {
      method: "POST",
      headers: { "Content-Type": "application/json", Accept: "application/json" },
      credentials: "include",
      body: JSON.stringify({ email }),
    });
  } catch {
    return { ok: false, message: "network" };
  }
  if (response.status === 429) {
    return { ok: false, rateLimited: true, message: "rateLimited" };
  }
  // Any 2xx (the API returns 204) advances; anything else is a generic failure (still no enumeration).
  if (!response.ok) {
    return { ok: false, message: "generic" };
  }
  const body = (await response.json().catch(() => null)) as { devCode?: string } | null;
  return { ok: true, devCode: body?.devCode };
}

/**
 * Step 2 of the foster magic-code flow: verify the code. On success the API returns an access token
 * AND sets the httpOnly refresh cookie. Stores the access token in memory.
 */
export async function verifyMagicCode(
  email: string,
  code: string,
): Promise<{ ok: true } | { ok: false; rateLimited?: boolean; message: string }> {
  if (!email || !code) {
    return { ok: false, message: "enterCode" };
  }
  let response: Response;
  try {
    response = await fetch(`${API_URL}/auth/magic/verify`, {
      method: "POST",
      headers: { "Content-Type": "application/json", Accept: "application/json" },
      credentials: "include",
      body: JSON.stringify({ email, code }),
    });
  } catch {
    return { ok: false, message: "network" };
  }
  if (response.status === 429) {
    return { ok: false, rateLimited: true, message: "rateLimited" };
  }
  if (!response.ok) {
    return { ok: false, message: "invalidCode" };
  }
  const body = (await response.json().catch(() => null)) as TokenResponse | null;
  if (!body?.access_token) {
    return { ok: false, message: "generic" };
  }
  setAccessToken(body.access_token);
  return { ok: true };
}

/** Admin username/password login. Stores the access token; uniform failure (no field-level leak). */
export async function loginAdmin(
  username: string,
  password: string,
  remember = false,
): Promise<{ ok: true } | { ok: false; message: string }> {
  if (!username || !password) {
    return { ok: false, message: "enterCredentials" };
  }
  let response: Response;
  try {
    response = await fetch(`${API_URL}/auth/login`, {
      method: "POST",
      headers: { "Content-Type": "application/json", Accept: "application/json" },
      // credentials:include so the API's httpOnly refresh cookie (jmix-3b-2 sets it on admin login)
      // is stored for the next silent restore. `remember` (parity MED-1) asks the API for the longer
      // 30-day refresh-family window instead of the 8h default — the access TTL/rotation are unchanged.
      credentials: "include",
      body: JSON.stringify({ username, password, remember }),
    });
  } catch {
    return { ok: false, message: "network" };
  }
  if (response.status === 429) {
    // A throttle is not a credential verdict — surface it distinctly (it's username-independent, so it
    // leaks nothing about whether the account exists). Must come BEFORE the generic non-2xx collapse.
    return { ok: false, message: "rateLimited" };
  }
  if (!response.ok) {
    // 401 invalid_credentials and any other non-2xx collapse to one message (no enumeration).
    return { ok: false, message: "invalidCredentials" };
  }
  const body = (await response.json().catch(() => null)) as TokenResponse | null;
  if (!body?.access_token) {
    return { ok: false, message: "generic" };
  }
  setAccessToken(body.access_token);
  return { ok: true };
}

/**
 * jmix-7-3: consume an admin-issued password-reset token to set a new password. The token comes from
 * the SPA's URL fragment (never a cookie), so this POSTs it in the body to the public, CSRF-off
 * `/api/auth/set-password` bridge. Client-side length/confirm validation is the screen's job; this just
 * posts. The backend returns `204` on success and a UNIFORM `400 {error:"reset_failed"}` for every
 * failure (bad/expired/used token, weak password, rate-limit) — so a `resetFailed` result here must
 * surface ONE "link invalid or expired" message (anti-oracle); never decode which reason it was.
 */
export async function setPassword(
  token: string,
  newPassword: string,
): Promise<{ ok: true } | { ok: false; message: "network" | "resetFailed" }> {
  let response: Response;
  try {
    response = await fetch(`${API_URL}/auth/set-password`, {
      method: "POST",
      headers: { "Content-Type": "application/json", Accept: "application/json" },
      credentials: "include",
      body: JSON.stringify({ token, newPassword }),
    });
  } catch {
    // A transient reachability problem — not a token verdict; the screen lets the operator retry.
    return { ok: false, message: "network" };
  }
  if (response.ok) {
    // 204 No Content on success.
    return { ok: true };
  }
  // Any non-2xx (the uniform 400, a 429, anything) collapses to the single anti-oracle outcome.
  return { ok: false, message: "resetFailed" };
}

/**
 * Silently restore a session on startup. The access token lives in memory only, so a reload boots
 * with none; this re-mints one from the httpOnly refresh cookie (POST /api/auth/refresh, CSRF-primed).
 * Returns true when a token was obtained — call once before first render so `check` sees the restored
 * session and the app renders authed instead of flashing the login screen. Fails closed + silent on
 * no/expired cookie; a stale CSRF token is re-primed once before giving up.
 */
async function postRefresh(): Promise<Response> {
  return fetch(`${API_URL}/auth/refresh`, {
    method: "POST",
    headers: { Accept: "application/json", ...(await csrfHeaders()) },
    credentials: "include",
  });
}

export async function restoreSession(): Promise<boolean> {
  let response: Response;
  try {
    response = await postRefresh();
    if (response.status === 403) {
      // Stale/missing CSRF token — re-prime once and retry.
      clearCsrfToken();
      response = await postRefresh();
    }
  } catch {
    return false;
  }
  if (!response.ok) {
    return false;
  }
  const body = (await response.json().catch(() => null)) as TokenResponse | null;
  if (!body?.access_token) {
    return false;
  }
  setAccessToken(body.access_token);
  return true;
}

export const authProvider: AuthProvider = {
  login: async (params) => {
    const p = (params ?? {}) as LoginParams;
    const result = isAdminLogin(p)
      ? await loginAdmin(p.username, p.password, p.remember ?? false)
      : await verifyMagicCode(p.email, p.code);
    if (result.ok) {
      return { success: true, redirectTo: "/" };
    }
    return {
      success: false,
      error: { name: "Login failed", message: result.message },
    };
  },

  logout: async () => {
    // Revoke the refresh family + clear the cookie server-side (jmix-3b-2). The endpoint is
    // CSRF-protected, so prime first. NEVER block logout on the network — clearing the in-memory token
    // below is what actually logs the operator out, even if the revoke call fails.
    try {
      await fetch(`${API_URL}/auth/logout`, {
        method: "POST",
        headers: { ...(await csrfHeaders()) },
        credentials: "include",
      });
    } catch {
      // ignore — the in-memory clear is authoritative
    }
    clearCsrfToken();
    clearAccessToken();
    // Login split: the persona-agnostic fallback is the foster (public) login; the Gate canonicalises
    // the address bar per-role (admin/self-service → /aerie) on logout. See auth/loginRoute.ts.
    return { success: true, redirectTo: FOSTER_LOGIN_PATH };
  },

  check: async () => {
    if (getAccessToken()) {
      return { authenticated: true };
    }
    return { authenticated: false, redirectTo: FOSTER_LOGIN_PATH, logout: true };
  },

  onError: async (error) => {
    const status =
      (error as { statusCode?: number })?.statusCode ?? (error as { status?: number })?.status;
    // 401/403 → the session is gone/forbidden: log out. A 409 is a version/branch conflict the screen
    // handles (FR-branch handling) — NOT a logout.
    if (status === 401 || status === 403) {
      return { logout: true, redirectTo: FOSTER_LOGIN_PATH, error };
    }
    return {};
  },

  getIdentity: async () => {
    const token = getAccessToken();
    if (!token) {
      return null;
    }
    const claims = decodeJwtClaims(token);
    if (!claims) {
      return { id: "user" };
    }
    return {
      id: (claims.sub as string) ?? "user",
      name:
        (claims.name as string) ?? (claims.preferred_username as string) ?? (claims.sub as string),
      email: (claims.email as string) ?? undefined,
    };
  },

  getPermissions: async () => {
    return authoritiesFromToken(getAccessToken());
  },
};

/** Convenience for the shell: the current PRIMARY nav Role (precedence winner) — null if none. */
export function currentRole() {
  return roleFromAuthorities(authoritiesFromToken(getAccessToken()));
}

/** All current nav Roles the in-memory token grants — the shell's nav is additive over these (3a-T2). */
export function currentRoles() {
  return rolesFromAuthorities(authoritiesFromToken(getAccessToken()));
}
